import { useTranslation } from 'react-i18next'
import { AvailabilityBadge, ConfiguredLimitBadge, PriceOrDiscountBlock, ServiceStatusBadge } from '@/shared/components/cse'
import { formatIsoDateDisplay } from '@/shared/lib/datetime'
import { formatMoneyCents } from '@/shared/lib/formatMoney'
import { createLazyThemedIcon } from '@/shared/theme/resolveThemedIcon'
import type { ColonySession } from '@/features/colony/schemas/colony.schema'
import { HorizonMediaCard } from '../../../shared/components/HorizonMediaCard'

const CalendarIcon = createLazyThemedIcon('calendar')

export function HorizonColonyCard({
  session,
  priority = false,
}: {
  session: ColonySession
  priority?: boolean
}) {
  const { t } = useTranslation()
  const detailTo = `/colony/sessions/${encodeURIComponent(session.id)}`

  return (
    <HorizonMediaCard
      to={detailTo}
      title={session.name}
      subtitle={t('colony.session_dates', {
        from: formatIsoDateDisplay(session.starts_on),
        to: formatIsoDateDisplay(session.ends_on),
      })}
      badges={
        <>
          <ServiceStatusBadge
            label={session.is_open ? t('horizon.colony.card.status_open') : t('horizon.colony.card.status_closed')}
            tone={session.is_open ? 'open' : 'closed'}
          />
          {session.spots_remaining !== null ? (
            <AvailabilityBadge
              label={t('horizon.colony.card.spots', { count: session.spots_remaining })}
              available={session.spots_remaining > 0}
            />
          ) : null}
        </>
      }
      details={
        <ConfiguredLimitBadge
          label={t('colony.session_age_range', { min: session.min_age, max: session.max_age })}
        />
      }
      price={
        <PriceOrDiscountBlock
          bare
          value={formatMoneyCents(session.price_cents, 'EUR')}
          helper={t('horizon.colony.card.price_helper')}
        />
      }
      actionLabel={t('colony.view')}
      actionAriaLabel={t('colony.view_aria', { name: session.name })}
      fallbackIcon={CalendarIcon}
      priority={priority}
    />
  )
}
